import {
  AiCard,
  AiLinkCardGroup,
  AiLoadingPanel,
  AiPanel,
  Button,
  Css,
  Tokens,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { useNavigate } from "react-router-dom";
import { ImportFromDocumentModal } from "src/components/uploader/ImportFromDocumentModal";
import { AiProposalStatus, AssetInput } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  useCreatePlanPackageAiProposalMutation,
  usePlanPackageAiProposalStatusQuery,
} from "src/routes/libraries/plan-package-v2/components/overview/PlanPackageDocumentImportPanel.generated";
import { AiProposalTab, summarizeAiProposal } from "src/routes/libraries/plan-package-v2/planPackageAiProposalSummary";
import { PlanPackageDetailsParams } from "src/routes/routesDef";
import {
  planPackageElevationsRoute,
  planPackageLocationsRoute,
  planPackageOptionsRoute,
  planPackageProgramDataRoute,
} from "src/routing/domains/libraries/plan-packages/planPackagesPaths";
import { formatMonthDayYear } from "src/utils/dates";

/** Invites the user to import plan data from their Construction Documents, then reports on the import. */
export function PlanPackageDocumentImportPanel() {
  const params = useRouteParams<PlanPackageDetailsParams>();
  const { planPackageId } = params;
  const { openModal } = useModal();
  const navigate = useNavigate();
  const tid = useTestIds({}, "planPackageDocumentImportPanel");
  const [createProposal] = useCreatePlanPackageAiProposalMutation();
  const { data, refetch, startPolling, stopPolling } = usePlanPackageAiProposalStatusQuery({
    variables: { id: planPackageId },
  });
  const proposal = data?.planPackage.aiProposal;
  const isProcessing = proposal?.status.code === AiProposalStatus.Pending;
  isProcessing ? startPolling(10_000) : stopPolling();
  const summaries = summarizeAiProposal(proposal);

  async function onImport(asset: AssetInput) {
    await createProposal({ variables: { input: { planPackageId, asset } } });
    // The new proposal isn't in this query's cached `planPackage`, so go get it
    await refetch();
  }

  if (isProcessing) {
    return (
      <AiLoadingPanel
        title="We're processing your document..."
        message="If you leave this page the processing will continue and your results will show up here for your review and acceptance. You can overwrite any imported content."
        progressText={proposal.progressText ?? undefined}
      />
    );
  }

  if (proposal) {
    const documentLink = (
      <a href={proposal.asset.downloadUrl} target="_blank" rel="noreferrer" {...tid.documentLink}>
        {proposal.asset.fileName ?? "your document"}
      </a>
    );
    return (
      <AiLinkCardGroup
        cards={summaries.map(({ tab, title }) => {
          const { route, message: tabMessage } = tabInfo[tab];
          return {
            title,
            message: (
              <>
                According to the latest document upload {documentLink} on {formatMonthDayYear(proposal.createdAt)},{" "}
                {tabMessage}
              </>
            ),
            action: { onClick: () => navigate(route.url(params)) },
          };
        })}
      />
    );
  }

  return (
    <AiPanel rounded padding="lg">
      <AiCard size="lg">
        <div css={Css.df.fdc.gap1.px3.py2.$}>
          <span css={Css.lg.aiBoldText.$} {...tid.title}>
            Import Plan Data from your Construction Documents
          </span>
          <span css={Css.sm.color(Tokens.OnSurface).$} {...tid.message}>
            Upload your Construction Documents and AI will extract and fill in Elevations, Locations, Options and
            Program Data to your Plan Package. This process takes about 5 minutes. Then you'll review and accept the
            results.
          </span>
          <div css={Css.pt2.$}>
            <Button
              label="Upload Document"
              icon="upload"
              onClick={() =>
                openModal({
                  size: "lg",
                  aiMode: true,
                  drawHeaderBorder: true,
                  content: <ImportFromDocumentModal onImport={onImport} />,
                })
              }
            />
          </div>
        </div>
      </AiCard>
    </AiPanel>
  );
}

const tabInfo: Record<
  AiProposalTab,
  {
    route:
      | typeof planPackageElevationsRoute
      | typeof planPackageLocationsRoute
      | typeof planPackageOptionsRoute
      | typeof planPackageProgramDataRoute;
    message: string;
  }
> = {
  elevations: {
    route: planPackageElevationsRoute,
    message: "elevations have been added or changed based on the latest construction document",
  },
  locations: { route: planPackageLocationsRoute, message: "locations have been added, removed, or renumbered" },
  options: {
    route: planPackageOptionsRoute,
    message: "options and their configurations have changed based on the latest construction document",
  },
  programData: { route: planPackageProgramDataRoute, message: "plan-level program data has changed" },
};
